import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { formatINR } from "@/lib/utils";
import { adjustText } from "./PriceListsPage";

interface Item { id: string; product_id: string; product_name: string; product_code: string; unit: string; catalog_price: number; min_qty: number; unit_price: number }
interface Detail { id: string; name: string; description?: string | null; adjust_pct: number | string; valid_from?: string | null; valid_to?: string | null; status: boolean; usable_today: boolean; items: Item[] }

const th = "h-[34px] border-b border-border px-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const td = "border-b border-border/60 px-2.5 py-2";

export function PriceListDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [list, setList] = useState<Detail | null>(null);
  useRecordCrumb(list?.name);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", adjust_pct: "0", valid_from: "", valid_to: "", status: true });
  const [add, setAdd] = useState({ product_id: "", label: "", min_qty: "1", unit_price: "", catalog: 0 });

  const load = async () => {
    if (!id) return;
    try {
      setList(unwrapObject<Detail>(await api.get(`/api/PriceList/ById/${id}`)));
    } catch (e: any) {
      error("Could not open the price list", e?.message || "Not found");
      navigate("/price-lists");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!list) return <FormSkeleton />;

  const openEdit = () => {
    setForm({ name: list.name, description: list.description || "", adjust_pct: String(Number(list.adjust_pct)), valid_from: list.valid_from?.slice(0, 10) || "", valid_to: list.valid_to?.slice(0, 10) || "", status: list.status });
    setEditOpen(true);
  };

  const run = async (fn: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      await fn();
      success(done, "");
      await load();
      return true;
    } catch (e: any) {
      error("Could not do that", e?.message || "Try again");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () => api.put(`/api/PriceList/Update/${list.id}`, { name: form.name.trim(), description: form.description.trim(), adjust_pct: Number(form.adjust_pct) || 0, valid_from: form.valid_from || null, valid_to: form.valid_to || null, status: form.status }),
      "Price list saved",
    );
    if (ok) setEditOpen(false);
  };

  const addPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!add.product_id || add.unit_price === "") return;
    const ok = await run(() => api.put(`/api/PriceList/Item/${list.id}`, { product_id: add.product_id, min_qty: Number(add.min_qty) || 1, unit_price: Number(add.unit_price) }), "Price saved");
    if (ok) setAdd({ product_id: "", label: "", min_qty: "1", unit_price: "", catalog: 0 });
  };

  const removeList = async () => {
    if (!window.confirm(`Remove "${list.name}"? If customers or quotes use it, it is switched off instead.`)) return;
    setBusy(true);
    try {
      const r: any = await api.delete(`/api/PriceList/Delete/${list.id}`);
      const d = r?.AddtionalData ?? r?.data ?? r;
      success(d?.switched_off ? "Switched off" : "Removed", list.name);
      if (d?.switched_off) await load();
      else navigate("/price-lists");
    } catch (e: any) {
      error("Could not remove", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-10">
      <section className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="space-y-1">
            <Link to="/price-lists" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="size-3.5" /> All price lists</Link>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{list.name}</h1>
              {!list.status ? <Badge variant="outline">Off</Badge> : list.usable_today ? <Badge variant="success">In use</Badge> : <Badge variant="warning">Outside its dates</Badge>}
            </div>
            <p className="text-[13px] text-muted-foreground">
              Products not listed below: <strong className="text-foreground">{adjustText(list.adjust_pct)}</strong>
              {(list.valid_from || list.valid_to) && ` · valid ${list.valid_from?.slice(0, 10) || "from the start"} to ${list.valid_to?.slice(0, 10) || "no end"}`}
              {list.description && ` · ${list.description}`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openEdit} disabled={busy}><Pencil className="size-4 mr-1.5" /> Edit list</Button>
            <Button variant="outline" size="sm" className="text-destructive" onClick={removeList} disabled={busy}><Trash2 className="size-4 mr-1.5" /> Remove</Button>
          </div>
        </div>
      </section>

      <form onSubmit={addPrice} className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4">
        <div className="min-w-[240px] flex-1 space-y-1.5">
          <Label className="text-xs">Product</Label>
          <SearchSelect
            endpoint="/api/Product/List"
            extraParams={{ active: "true" }}
            value={add.product_id}
            selectedLabel={add.label}
            getLabel={(row) => `${row.name} (${row.code}) · catalog ${formatINR(row.unit_price)}`}
            placeholder="Search products"
            onChange={(pid, row) => setAdd({ ...add, product_id: pid, label: row ? `${row.name} (${row.code})` : "", catalog: row ? Number(row.unit_price) : 0, unit_price: add.unit_price || (row ? String(Number(row.unit_price)) : "") })}
          />
        </div>
        <div className="w-28 space-y-1.5"><Label className="text-xs">From quantity</Label><Input type="number" min={1} value={add.min_qty} onChange={(e) => setAdd({ ...add, min_qty: e.target.value })} /></div>
        <div className="w-36 space-y-1.5"><Label className="text-xs">Price (₹, before GST)</Label><Input type="number" min={0} step="0.01" value={add.unit_price} onChange={(e) => setAdd({ ...add, unit_price: e.target.value })} /></div>
        <Button type="submit" disabled={busy || !add.product_id || add.unit_price === ""}>{busy ? <Loader2 className="size-4 animate-spin mr-2" /> : <Plus className="size-4 mr-1.5" />}Set price</Button>
      </form>

      <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
        <table className="w-full min-w-[560px] border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-muted/40">
              <th className={th}>Product</th>
              <th className={th}>From quantity</th>
              <th className={`${th} text-right`}>List price</th>
              <th className={`${th} text-right`}>Catalog price</th>
              <th className={`${th} text-right`}>Difference</th>
              <th className={`${th} w-[50px]`}><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((i) => {
              const diff = i.catalog_price ? Math.round(((i.unit_price - i.catalog_price) / i.catalog_price) * 1000) / 10 : 0;
              return (
                <tr key={i.id}>
                  <td className={`${td} font-medium`}>{i.product_name} <span className="text-muted-foreground font-mono text-[11px]">{i.product_code}</span></td>
                  <td className={td}>{i.min_qty}+ {i.unit}</td>
                  <td className={`${td} text-right font-semibold`}>{formatINR(i.unit_price)}</td>
                  <td className={`${td} text-right text-muted-foreground`}>{formatINR(i.catalog_price)}</td>
                  <td className={`${td} text-right ${diff < 0 ? "text-success" : diff > 0 ? "text-warning" : ""}`}>{diff > 0 ? "+" : ""}{diff}%</td>
                  <td className={td}>
                    <Button variant="ghost" size="icon-sm" aria-label={`Remove price for ${i.product_name}`} onClick={() => run(() => api.delete(`/api/PriceList/Item/${list.id}/${i.id}`), "Price removed")}><Trash2 className="size-3.5 text-danger" /></Button>
                  </td>
                </tr>
              );
            })}
            {list.items.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No prices set yet. Every product is {adjustText(list.adjust_pct).toLowerCase()}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit price list</DialogTitle>
            <DialogDescription>Quotes already made keep the prices they were given.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} required /></div>
            <div className="space-y-1.5"><Label>Products you don't price (%)</Label><Input type="number" min={-90} max={500} step="0.5" value={form.adjust_pct} onChange={(e) => setForm({ ...form, adjust_pct: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Valid from</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Valid to</Label><Input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.status} onChange={(e) => setForm({ ...form, status: e.target.checked })} /> Switched on</label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
